import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/store/auth";
import { setDashboardLoadbarSource } from "@/composables/useDashboardLoadbar";

const PRIVILEGE = {
  MANAGE_USERS: 'MANAGE_USERS',
  MANAGE_CATALOGS: 'MANAGE_CATALOGS',
  ACCESS_PROFILE: 'ACCESS_PROFILE',
  ACCESS_DIGITAL_SIGNATURE: 'ACCESS_DIGITAL_SIGNATURE',
  ACCESS_REPORT_TEMPLATES: 'ACCESS_REPORT_TEMPLATES',
  ACCESS_STUDIES: 'ACCESS_STUDIES',
  ACCESS_PATIENTS: 'ACCESS_PATIENTS',
  ACCESS_SITES: 'ACCESS_SITES',
  MANAGE_APP_RELEASES: 'MANAGE_APP_RELEASES',
  VIEW_ALL_ACTIVITY_LOGS: 'VIEW_ALL_ACTIVITY_LOGS',
  SEND_ALERTS: 'SEND_ALERTS',
} as const;

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/login",
      name: "Login",
      component: () => import("@/views/LoginView.vue"),
      meta: { guest: true },
    },
    {
      path: "/signup",
      name: "SignUp",
      component: () => import("@/views/SignUpView.vue"),
      meta: { guest: true },
    },
    {
      path: "/verify",
      name: "VerifyOtp",
      component: () => import("@/views/VerifyOtpView.vue"),
      meta: { guest: true, requiresPending: true },
    },
    {
      path: "/forgot-password",
      name: "ForgotPassword",
      component: () => import("@/views/ForgotPasswordView.vue"),
      meta: { guest: true },
    },
    {
      path: "/",
      name: "Dashboard",
      component: () => import("@/views/DashboardView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/patients",
      name: "Patients",
      component: () => import("@/views/PacientesView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.ACCESS_PATIENTS },
    },
    {
      path: "/studies",
      name: "Studies",
      component: () => import("@/views/EstudiosView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.ACCESS_STUDIES },
    },
    {
      path: "/users",
      name: "Users",
      component: () => import("@/views/UsuariosView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.MANAGE_USERS },
    },
    {
      path: "/monitor-templates",
      name: "MonitorTemplates",
      component: () => import("@/views/MonitorTemplatesView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.MANAGE_USERS },
    },
    {
      path: "/site-locations",
      name: "SiteLocations",
      component: () => import("@/views/SiteLocationsView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.MANAGE_USERS },
    },
    {
      path: "/role-qualifications",
      name: "RoleQualifications",
      component: () => import("@/views/RoleQualificationsView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.MANAGE_USERS },
    },
    {
      path: "/catalogs",
      name: "Catalogs",
      component: () => import("@/views/CatalogView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.MANAGE_CATALOGS },
    },
    {
      path: "/privileges",
      name: "Privileges",
      component: () => import("@/views/PrivilegesView.vue"),
      meta: {
        requiresAuth: true,
        requiresAnyPrivilege: [PRIVILEGE.MANAGE_USERS, PRIVILEGE.MANAGE_CATALOGS],
      },
    },
    {
      path: "/notifications",
      name: "Notifications",
      component: () => import("@/views/NotificationsView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/alerts",
      name: "Alerts",
      component: () => import("@/views/AlertsView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.SEND_ALERTS },
    },
    {
      path: "/activity-logs",
      name: "ActivityLogs",
      component: () => import("@/views/ActivityLogsView.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/sites",
      name: "Sites",
      component: () => import("@/views/SitesView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.ACCESS_SITES },
    },
    {
      path: "/physicians",
      name: "Physicians",
      component: () => import("@/views/PhysiciansView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.ACCESS_SITES },
    },
    {
      path: "/profile",
      name: "Profile",
      component: () => import("@/views/ProfileView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.ACCESS_PROFILE },
    },
    {
      path: "/templates",
      name: "ReportTemplates",
      component: () => import("@/views/ReportTemplatesView.vue"),
      meta: { requiresAuth: true, requiresPrivilege: PRIVILEGE.ACCESS_REPORT_TEMPLATES },
    },
    {
      path: "/app-releases",
      name: "AppReleases",
      component: () => import("@/views/AppReleasesView.vue"),
      meta: {
        requiresAuth: true,
        requiresPrivilege: PRIVILEGE.MANAGE_APP_RELEASES,
      },
    },
    {
      path: "/:pathMatch(.*)*",
      name: "NotFound",
      component: () => import("@/views/NotFoundView.vue"),
    },
  ],
});

/** Green loadbar on every authenticated menu / page navigation (all roles). */
const ROUTE_LOADBAR_MIN_MS = 320;
let routeLoadbarTimer: ReturnType<typeof setTimeout> | null = null;

function isAppShellRoute(route: { meta: Record<string, unknown> }) {
  return Boolean(route.meta.requiresAuth);
}

function clearRouteLoadbarTimer() {
  if (routeLoadbarTimer != null) {
    clearTimeout(routeLoadbarTimer);
    routeLoadbarTimer = null;
  }
}

// Register BEFORE auth guard so the bar shows during privilege refresh / lazy imports.
router.beforeEach((to, from) => {
  if (to.fullPath === from.fullPath) return;
  if (!isAppShellRoute(to) && !isAppShellRoute(from)) return;
  clearRouteLoadbarTimer();
  setDashboardLoadbarSource("route", true);
});

// Navigation guard — espera a que auth esté inicializado
let isFirstNavigation = true;

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // En la primera navegación, esperar a que initializeAuth() termine
  if (isFirstNavigation) {
    isFirstNavigation = false;
    // Esperar hasta que el flag 'initialized' sea true
    const maxWait = Date.now() + 5000; // timeout 5s
    while (!authStore.initialized && Date.now() < maxWait) {
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  // Ahora que está inicializado, evaluar rutas correctamente

  // Si tiene auth verificado y va a guest → al dashboard
  if (to.meta.guest && authStore.isAuthenticated) {
    next("/");
    return;
  }

  // Ruta /verify: requiere pending_email
  if (to.meta.requiresPending && !authStore.pendingEmail) {
    next("/login");
    return;
  }

  // Ruta protegida: requiere auth verificado
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Si hay pending email, ir a verify
    if (authStore.pendingEmail) {
      next("/verify");
      return;
    }
    next("/login");
    return;
  }

  // Refrescar privilegios desde el servidor antes de evaluar acceso restringido
  const needsPrivilegeCheck = Boolean(to.meta.requiresPrivilege || to.meta.requiresAnyPrivilege);
  if (needsPrivilegeCheck && authStore.isAuthenticated) {
    await authStore.refreshUser();
  }

  // Rutas protegidas por privilegio
  if (to.meta.requiresPrivilege) {
    const required = to.meta.requiresPrivilege as string;
    if (!authStore.hasPrivilege(required)) {
      next("/");
      return;
    }
  }

  if (to.meta.requiresAnyPrivilege) {
    const required = to.meta.requiresAnyPrivilege as string[];
    if (!authStore.hasAnyPrivilege(required)) {
      next("/");
      return;
    }
  }

  next();
});

router.afterEach(() => {
  clearRouteLoadbarTimer();
  routeLoadbarTimer = setTimeout(() => {
    setDashboardLoadbarSource("route", false);
    routeLoadbarTimer = null;
  }, ROUTE_LOADBAR_MIN_MS);
});

router.onError(() => {
  clearRouteLoadbarTimer();
  setDashboardLoadbarSource("route", false);
});

export default router;
